import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Heading, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle, Spinner, Stack, Text, TextLink,
} from '@ui'
import { deleteJSON, getJSON, postJSON } from '../api.js'
import { useAuth } from '../auth.jsx'
import { formatClipLength, walkCaption } from '../components/Photos.jsx'
import { formatDate } from '../format.js'

// Admin only (ADMIN_EMAILS; the server checks every request). Photos and videos deleted on the
// site (a soft delete: the files are still in the bucket) can be restored, or deleted
// permanently: the files and the row go, and a tombstone stops the file being added again by an
// upload or import. See site/CLAUDE.md "Deleted photos".

const adminUrl = (id, size) => `/api/admin/photos/${id}/${size}`

function formatBytes(n) {
  if (n == null) return ''
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GB`
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(n / 1024))} KB`
}

const plural = (n, word) => `${n.toLocaleString('en-GB')} ${word}${n === 1 ? '' : 's'}`

export default function DeletedPhotos() {
  const { admin } = useAuth()
  const [photos, setPhotos] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(null) // "Restoring 2 of 5…"
  const [failures, setFailures] = useState([])

  useEffect(() => {
    if (!admin) return undefined
    const controller = new AbortController()
    getJSON('/api/admin/photos/deleted', { signal: controller.signal })
      .then(setPhotos)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [admin])

  const chosen = useMemo(() => (photos ?? []).filter((p) => selected.has(p.id)), [photos, selected])
  const totalBytes = (list) => list.reduce((sum, p) => sum + (p.size_bytes ?? 0), 0)

  if (!admin) return <Alert variant="secondary">This page is only for the site's admin.</Alert>
  if (error) return <Alert variant="danger">Couldn't load deleted photos: {error}</Alert>
  if (!photos) return <Spinner />

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  // Runs one request per photo, in turn, so a big batch shows progress and one failure doesn't
  // stop the rest. Photos that succeeded leave the list.
  const run = async (verb, send) => {
    const list = chosen
    const done = new Set()
    const failed = []
    for (const [n, p] of list.entries()) {
      setBusy(`${verb} ${n + 1} of ${list.length}…`)
      try {
        await send(p)
        done.add(p.id)
      } catch (err) {
        failed.push(`${walkCaption(p)}${p.original_name ? `, ${p.original_name}` : ''}: ${err.message}`)
      }
    }
    setPhotos((all) => all.filter((p) => !done.has(p.id)))
    setSelected(new Set([...selected].filter((id) => !done.has(id))))
    setFailures(failed)
    setBusy(null)
  }
  const restore = () => run('Restoring', (p) => postJSON(`/api/admin/photos/${p.id}/restore`))
  const purge = () => {
    setConfirming(false)
    run('Deleting', (p) => deleteJSON(`/api/admin/photos/${p.id}`))
  }

  const allSelected = photos.length > 0 && selected.size === photos.length

  return (
    <>
      <Stack wrap align="center" gap={2} mb={2}>
        <Heading size={3} mb={0} me="auto">Deleted photos</Heading>
        <TextLink to="/photos" linkTone="muted" text="small">Back to photos</TextLink>
      </Stack>
      <Text as="p" muted text="small">
        {photos.length === 0
          ? 'Nothing deleted.'
          : `${plural(photos.length, 'item')}, ${formatBytes(totalBytes(photos))} in storage. Restore puts one back where it was; Delete permanently removes its files for good, and it won't be added again by an upload or import.`}
      </Text>

      {failures.length > 0 && (
        <Alert variant="warning" dismissible onClose={() => setFailures([])}>
          {plural(failures.length, 'item')} didn't go through:
          <Box as="ul" mb={0}>{failures.map((f) => <li key={f}>{f}</li>)}</Box>
        </Alert>
      )}

      {photos.length > 0 && (
        <Stack wrap align="center" gap={2} mb={3} className="deleted-toolbar">
          <Checkbox
            id="select-all" label={allSelected ? 'Select none' : 'Select all'} checked={allSelected} disabled={!!busy}
            onChange={() => setSelected(allSelected ? new Set() : new Set(photos.map((p) => p.id)))}
          />
          <Text muted text="small" me="auto">
            {chosen.length > 0 && `${chosen.length} selected, ${formatBytes(totalBytes(chosen))}`}
          </Text>
          {busy && <Text text="small"><Spinner size="sm" me={1} />{busy}</Text>}
          <Button size="sm" variant="secondary" outline disabled={!chosen.length || !!busy} onClick={restore}>Restore</Button>
          <Button size="sm" variant="danger" outline disabled={!chosen.length || !!busy} onClick={() => setConfirming(true)}>Delete permanently…</Button>
        </Stack>
      )}

      <div className="deleted-grid">
        {photos.map((p) => {
          const video = p.media_type === 'video'
          return (
            <div key={p.id} className={`deleted-item${selected.has(p.id) ? ' selected' : ''}`}>
              <button
                type="button" className="photo-thumb" onClick={() => toggle(p.id)} disabled={!!busy}
                aria-pressed={selected.has(p.id)} aria-label={`${selected.has(p.id) ? 'Unselect' : 'Select'} ${video ? 'video' : 'photo'} from ${walkCaption(p)}`}
              >
                {p.has_preview === false
                  ? <span className="photo-placeholder" aria-hidden="true">{video ? 'Video' : 'Photo'}</span>
                  : <img src={adminUrl(p.id, 'thumb')} alt="" loading="lazy" />}
                {video && (
                  <span className="photo-video-badge" aria-hidden="true">
                    <svg viewBox="0 0 10 10" width="0.6em" height="0.6em" fill="currentColor"><path d="M1 0.5v9l8-4.5z" /></svg>
                    {p.duration_ms != null && ` ${formatClipLength(p.duration_ms)}`}
                  </span>
                )}
                <span className="deleted-check" aria-hidden="true">{selected.has(p.id) ? '✓' : ''}</span>
              </button>
              <Box text="small" mt={1} tight>
                <TextLink to={`/walks/${p.walk_id}`} linkTone="muted">{walkCaption(p)}</TextLink>
                <Box muted>
                  Deleted {formatDate(p.deleted_at)} · {formatBytes(p.size_bytes)}
                  {' · '}<TextLink href={adminUrl(p.id, 'original')} target="_blank" rel="noreferrer" linkTone="muted">open</TextLink>
                </Box>
              </Box>
            </div>
          )
        })}
      </div>

      <Modal show={confirming} onHide={() => setConfirming(false)} centered>
        <ModalHeader><ModalTitle as="h2" size={5}>Delete permanently?</ModalTitle></ModalHeader>
        <ModalBody>
          {plural(chosen.length, 'item')} ({formatBytes(totalBytes(chosen))}) will be removed from storage for
          good. This can't be undone. If the same file is uploaded or imported again, it will be left out.
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="danger" onClick={purge}>Delete permanently</Button>
        </ModalFooter>
      </Modal>
    </>
  )
}
